import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { components } from "@/api/contract";
import { queryKeys } from "@/api/query-keys";
import { getVideoJob } from "../api/getVideoJob";

// Video generation is slower than images — poll a touch less often and cap the
// wait at ~4 minutes (the heaviest models can take a couple of minutes).
const POLL_INTERVAL_MS = 3_000;
const TIMEOUT_MS = 240_000;

/** Terminal phase of a polled video job — drives the chat tool card. */
export type VideoJobPhase = "generating" | "succeeded" | "failed";

/** A single produced video (url + poster + dimensions + ThumbHash blur). */
export type VideoJobOutput = components["schemas"]["VideoOutput"];

/** Phase + first produced output — the tool card renders the placeholder→video. */
export interface VideoJobStatus {
  phase: VideoJobPhase;
  output: VideoJobOutput | null;
}

/**
 * Polls an async `generate_video` job (`GET /v1/videos/jobs/{id}`) every ~3s
 * until it reaches a terminal state, capped at ~4 min. Returns the coarse phase
 * plus the first produced output so the tool card can render a skeleton-pulse →
 * inline video. Pass `null` to stay idle. Mirrors `useImageJobStatus`.
 */
export function useVideoJobStatus(jobId: string | null): VideoJobStatus {
  const startedAt = useRef<number>(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    startedAt.current = Date.now();
    setTimedOut(false);
  }, [jobId]);

  const { data } = useQuery({
    queryKey: queryKeys.chat.videoJob(jobId ?? ""),
    queryFn: () => getVideoJob(jobId as string),
    enabled: jobId !== null && !timedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "succeeded" || status === "failed" || status === "cancelled") return false;
      if (Date.now() - startedAt.current > TIMEOUT_MS) return false;
      return POLL_INTERVAL_MS;
    },
    staleTime: 0,
    gcTime: 0,
  });

  const status = data?.status;

  useEffect(() => {
    if (jobId === null || status === "succeeded" || status === "failed" || status === "cancelled") {
      return;
    }
    const remaining = TIMEOUT_MS - (Date.now() - startedAt.current);
    const id = setTimeout(() => setTimedOut(true), Math.max(0, remaining));
    return () => clearTimeout(id);
  }, [jobId, status]);

  const output = data?.outputs?.[0] ?? null;

  if (status === "succeeded") return { phase: "succeeded", output };
  if (status === "failed" || status === "cancelled") return { phase: "failed", output };
  if (timedOut) return { phase: "failed", output };
  return { phase: "generating", output };
}
